import {
  Card,
  ErrorAlert,
  LoadingAlert,
  YieldBearingAssetCompositionSection,
} from '@/components';
import YieldBearingAssetAboutSection from '@/components/organisms/YieldBearingAssetAboutSection/YieldBearingAssetAboutSection';
import { useYieldBearingAssetRawData } from '@/components/providers';
import { formatYieldDataForChart } from '@/utils/formatUtils';
import { logError, logInfo } from '@/utils/logger';
import { MESSAGES } from '@/utils/strings';
import { FC, useEffect, useMemo, useState } from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  rootGap: 'gap-sm',
  bodyPadding: 'p-0',
  gapBetweenBodyElementsInAboutSection: 'gap-sm',
  gapBetweenTextBlocksToRightInAboutSection: 'gap-sm',
};
interface Props {
  ticker: string;
  showHistoricalPerformance?: boolean;
  showComposition?: boolean;
}
interface HistoricalDataPoint {
  time: string;
  value: number;
  label: string;
}
const YieldBearingAssetPerformanceDetailCardBody: FC<Props> = ({
  ticker,
  showHistoricalPerformance = true,
  showComposition = false,
}) => {
  const { yieldBearingAssets, getAssetYieldData, loading, error } =
    useYieldBearingAssetRawData();
  const asset = useMemo(
    () => yieldBearingAssets.find((a) => a.ticker === ticker) || null,
    [ticker, yieldBearingAssets],
  );
  const [historicalData, setHistoricalData] = useState<HistoricalDataPoint[]>(
    [],
  );
  const [historicalDataLoading, setHistoricalDataLoading] = useState(false);
  const [historicalDataError, setHistoricalDataError] = useState<
    string | undefined
  >(undefined);
  useEffect(() => {
    if (loading || historicalDataLoading) {
      logInfo(
        'Loading yield bearing assets',
        'YieldBearingAssetPerformanceDetailCardBody.fetchHistoricalData',
      );
    }
  }, [loading, historicalDataLoading]);
  useEffect(() => {
    if (!ticker) return;
    let isCancelled = false;
    const metricKey = 'tvlUsd';
    const fetchHistoricalData = async () => {
      try {
        setHistoricalDataLoading(true);
        setHistoricalDataError(undefined);
        const yieldData = await getAssetYieldData(ticker, true);
        if (isCancelled) return;
        if (yieldData?.timeSeriesData) {
          setHistoricalData(
            formatYieldDataForChart(yieldData.timeSeriesData, metricKey),
          );
        } else {
          setHistoricalData([]);
        }
      } catch (error) {
        if (isCancelled) return;
        logError(
          error,
          'YieldBearingAssetPerformanceDetailCardBody.fetchHistoricalData',
        );
        setHistoricalDataError(
          'Failed to fetch historical data from DeFi Llama.',
        );
        setHistoricalData([]);
      } finally {
        if (!isCancelled) {
          setHistoricalDataLoading(false);
        }
      }
    };
    fetchHistoricalData();
    return () => {
      isCancelled = true;
    };
  }, [ticker, getAssetYieldData]);
  if ((loading || historicalDataLoading) && !asset) {
    return (
      <LoadingAlert
        title={MESSAGES.YIELD_BEARING_ASSET_DETAIL.LOADING_TITLE}
        description={MESSAGES.YIELD_BEARING_ASSET_DETAIL.LOADING_DESCRIPTION}
      />
    );
  }
  if (!asset && !loading) {
    return (
      <ErrorAlert
        title={MESSAGES.YIELD_BEARING_ASSET_DETAIL.ASSET_NOT_FOUND_ERROR_TITLE.replace(
          '[asset_ticker]',
          ticker,
        )}
        description={MESSAGES.YIELD_BEARING_ASSET_DETAIL.ASSET_NOT_FOUND_ERROR_DESCRIPTION.replace(
          '[asset_ticker]',
          ticker,
        )}
      />
    );
  }
  if (error || historicalDataError) {
    const errorTitle = error
      ? MESSAGES.YIELD_BEARING_ASSET_DETAIL.ERROR_LOADING_TITLE.replace(
          '[asset_ticker]',
          ticker,
        )
      : 'Error loading historical data';
    const errorDescription = error
      ? MESSAGES.YIELD_BEARING_ASSET_DETAIL.ERROR_LOADING_DESCRIPTION.replace(
          '[asset_ticker]',
          ticker,
        )
      : historicalDataError;
    return <ErrorAlert title={errorTitle} description={errorDescription} />;
  }
  return (
    <Card.Body>
      {asset && (
        <div
          className={`flex flex-col ${spacingConfig.rootGap} ${borderClassToVisualizeElementsAndSpacing}`}
        >
          <YieldBearingAssetAboutSection
            asset={asset}
            showShortDescription
            showHistoricalPerformance={showHistoricalPerformance}
          />
          {showComposition && (
            <YieldBearingAssetCompositionSection
              yieldBearingAsset={asset}
              underlyingYieldBearingAssets={asset.underlyingYieldBearingAssets}
              showHeader
              showDescription
              showCompositionDescription
              showSource={false}
              canChangeComposition={false}
            />
          )}
        </div>
      )}
    </Card.Body>
  );
};
export default YieldBearingAssetPerformanceDetailCardBody;
